import React, { Suspense } from 'react';
import {
    HomeOutlined,
    ShoppingOutlined,
    TruckOutlined,
    UserOutlined,
    EnvironmentOutlined,
    AimOutlined,
    GlobalOutlined,
    FileTextOutlined,
    TeamOutlined,
    DollarOutlined,
    GiftOutlined,
    TagOutlined,
    FileProtectOutlined,
    CrownOutlined,
    IdcardOutlined,
    WalletOutlined,
    TransactionOutlined,
    RocketOutlined,
    CarOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';

/**
 * Lazy-loaded page components
 */
const PageDashboard = React.lazy(() => import('@/pages/dashboard'));
const PageOrders = React.lazy(() => import('@/pages/orders'));
const PageWaybills = React.lazy(() => import('@/pages/waybills'));
const PageVehicles = React.lazy(() => import('@/pages/vehicles'));
const PageStations = React.lazy(() => import('@/pages/stations'));
const PageStops = React.lazy(() => import('@/pages/stops'));
const PageGeoFences = React.lazy(() => import('@/pages/geo-fences'));
const PageCustomers = React.lazy(() => import('@/pages/customers'));
const PageAutoUsers = React.lazy(() => import('@/pages/auto-users'));
const PageOperators = React.lazy(() => import('@/pages/operators'));
const PagePricing = React.lazy(() => import('@/pages/pricing'));
const PageCouponTemplates = React.lazy(() => import('@/pages/coupon-templates'));
const PageCoupons = React.lazy(() => import('@/pages/coupons'));
const PageInvoices = React.lazy(() => import('@/pages/invoices'));
const PageMembers = React.lazy(() => import('@/pages/members'));
const PageWallets = React.lazy(() => import('@/pages/enterprise/wallet'));
const PageWalletTransactions = React.lazy(() => import('@/pages/enterprise/wallet/transactions'));
const PageWalletDetail = React.lazy(() => import('@/pages/enterprise/wallet/detail'));
const PageVehicleModels = React.lazy(() => import('@/pages/vehicle-models'));
const PageCargoTypes = React.lazy(() => import('@/pages/cargo-types'));
const PageSimulateDispatch = React.lazy(() => import('@/pages/simulate-dispatch'));

/**
 * Types
 */
import type { MenuDataItem } from '@ant-design/pro-components';

const LazyLoad = ({ children }: { children: React.ReactNode }) => (
    <Suspense
        fallback={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 300 }}>
                <Spin size="large" />
            </div>
        }
    >
        {children}
    </Suspense>
);

const routes = [
    {
        element: <LazyLoad><PageDashboard /></LazyLoad>,
        icon: <HomeOutlined />,
        name: '仪表盘',
        path: '/',
    },
    {
        icon: <ShoppingOutlined />,
        name: '订单中心',
        path: '/order-center',
        children: [
            {
                element: <LazyLoad><PageOrders /></LazyLoad>,
                icon: <ShoppingOutlined />,
                name: '订单管理',
                path: 'orders',
            },
            {
                element: <LazyLoad><PageWaybills /></LazyLoad>,
                icon: <FileTextOutlined />,
                name: '运单管理',
                path: 'waybills',
            },
        ],
    },
    {
        icon: <TruckOutlined />,
        name: '车辆运营',
        path: '/vehicle-ops',
        children: [
            {
                element: <LazyLoad><PageVehicleModels /></LazyLoad>,
                icon: <CarOutlined />,
                name: '车型配置',
                path: 'vehicle-models',
            },
            {
                element: <LazyLoad><PageCargoTypes /></LazyLoad>,
                icon: <InboxOutlined />,
                name: '货物类型',
                path: 'cargo-types',
            },
            {
                element: <LazyLoad><PageVehicles /></LazyLoad>,
                icon: <TruckOutlined />,
                name: '车辆管理',
                path: 'vehicles',
            },
            {
                element: <LazyLoad><PageStations /></LazyLoad>,
                icon: <EnvironmentOutlined />,
                name: '站点管理',
                path: 'stations',
            },
            {
                element: <LazyLoad><PageStops /></LazyLoad>,
                icon: <AimOutlined />,
                name: '停靠点管理',
                path: 'stops',
            },
            {
                element: <LazyLoad><PageGeoFences /></LazyLoad>,
                icon: <GlobalOutlined />,
                name: '地理围栏',
                path: 'geo-fences',
            },
            {
                element: <LazyLoad><PageSimulateDispatch /></LazyLoad>,
                icon: <RocketOutlined />,
                name: '模拟派单',
                path: 'simulate-dispatch',
            },
        ],
    },
    {
        icon: <UserOutlined />,
        name: '用户管理',
        path: '/user-center',
        children: [
            {
                element: <LazyLoad><PageCustomers /></LazyLoad>,
                icon: <IdcardOutlined />,
                name: '客户管理',
                path: 'customers',
            },
            {
                element: <LazyLoad><PageAutoUsers /></LazyLoad>,
                icon: <UserOutlined />,
                name: '用户管理',
                path: 'auto-users',
            },
            {
                element: <LazyLoad><PageOperators /></LazyLoad>,
                icon: <TeamOutlined />,
                name: '操作员管理',
                path: 'operators',
            },
            {
                element: <LazyLoad><PageMembers /></LazyLoad>,
                icon: <CrownOutlined />,
                name: '会员管理',
                path: 'members',
            },
        ],
    },
    {
        icon: <DollarOutlined />,
        name: '财务中心',
        path: '/finance',
        children: [
            {
                element: <LazyLoad><PagePricing /></LazyLoad>,
                icon: <DollarOutlined />,
                name: '计价规则',
                path: 'pricing',
            },
            {
                element: <LazyLoad><PageCouponTemplates /></LazyLoad>,
                icon: <GiftOutlined />,
                name: '优惠券模板',
                path: 'coupon-templates',
            },
            {
                element: <LazyLoad><PageCoupons /></LazyLoad>,
                icon: <TagOutlined />,
                name: '优惠券管理',
                path: 'coupons',
            },
            {
                element: <LazyLoad><PageInvoices /></LazyLoad>,
                icon: <FileProtectOutlined />,
                name: '发票管理',
                path: 'invoices',
            },
            {
                element: <LazyLoad><PageWallets /></LazyLoad>,
                icon: <WalletOutlined />,
                name: '企业钱包',
                path: 'enterprise/wallets',
            },
            {
                element: <LazyLoad><PageWalletTransactions /></LazyLoad>,
                icon: <TransactionOutlined />,
                name: '账户流水',
                path: 'enterprise/wallet-transactions',
            },
            {
                element: <LazyLoad><PageWalletDetail /></LazyLoad>,
                hideInMenu: true,
                path: 'enterprise/wallets/:id',
            },
        ],
    },
] as MenuDataItem[];

export default routes;
